import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { telemedicineApi, TelemedicineSession, TelemedicineParticipant, TelemedicineToken } from '@/api/telemedicine-api';

export function useTelemedicineSession(sessionId?: string) {
  return useQuery<TelemedicineSession | null>({
    queryKey: ['telemedicine', 'session', sessionId],
    queryFn: () => sessionId ? telemedicineApi.getSession(sessionId) : Promise.resolve(null),
    enabled: !!sessionId,
    refetchInterval: 5000,
  });
}

export function useTelemedicineSessionByAppointment(appointmentId?: string) {
  return useQuery<TelemedicineSession | null>({
    queryKey: ['telemedicine', 'session-by-appointment', appointmentId],
    queryFn: () => appointmentId ? telemedicineApi.getSessionByAppointment(appointmentId) : Promise.resolve(null),
    enabled: !!appointmentId,
    refetchInterval: 5000,
  });
}

export function useGenerateToken() {
  return useMutation<TelemedicineToken, Error, { sessionId: string; userId: string; role: 'DOCTOR' | 'PATIENT' | 'OBSERVER' }>({
    mutationFn: (payload) => telemedicineApi.generateToken(payload),
  });
}

export function useJoinSession() {
  const queryClient = useQueryClient();
  return useMutation<TelemedicineSession, Error, { sessionId: string; userId: string; role: 'DOCTOR' | 'PATIENT' | 'OBSERVER' }>({
    mutationFn: (payload) => telemedicineApi.joinSession({ ...payload, ipAddress: 'browser' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['telemedicine', 'session', variables.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['telemedicine', 'participants', variables.sessionId] });
    },
  });
}

export function useLeaveSession() {
  const queryClient = useQueryClient();
  return useMutation<TelemedicineSession, Error, { sessionId: string; userId: string }>({
    mutationFn: (payload) => telemedicineApi.leaveSession(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['telemedicine', 'session', variables.sessionId] });
    },
  });
}

export function useStartSession() {
  const queryClient = useQueryClient();
  return useMutation<TelemedicineSession, Error, { sessionId: string; doctorId: string }>({
    mutationFn: (payload) => telemedicineApi.startSession(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['telemedicine', 'session', variables.sessionId] });
    },
  });
}

export function useEndSession() {
  const queryClient = useQueryClient();
  return useMutation<TelemedicineSession, Error, { sessionId: string; endedBy: string; reason?: string }>({
    mutationFn: (payload) => telemedicineApi.endSession(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['telemedicine', 'session', variables.sessionId] });
    },
  });
}

export function useCancelSession() {
  const queryClient = useQueryClient();
  return useMutation<TelemedicineSession, Error, { sessionId: string; cancelledBy: string; reason: string }>({
    mutationFn: (payload) => telemedicineApi.cancelSession(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['telemedicine', 'session', variables.sessionId] });
    },
  });
}

export function useSessionParticipants(sessionId?: string) {
  return useQuery<TelemedicineParticipant[]>({
    queryKey: ['telemedicine', 'participants', sessionId],
    queryFn: () => sessionId ? telemedicineApi.getSessionParticipants(sessionId) : Promise.resolve([]),
    enabled: !!sessionId,
    refetchInterval: 3000,
  });
}